import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';

import { registerUnsavedEditor } from './unsaved.ts';

export type AutosaveState = 'clean' | 'pending' | 'saving' | 'saved' | 'failed' | 'invalid';

const SUCCESS_EVENT = 'nightshift:settings-saved';

export function announceSettingsSaved(message = 'Settings saved'): void {
  window.dispatchEvent(new CustomEvent(SUCCESS_EVENT, { detail: message }));
}

/** One coalesced, deliberately short-lived acknowledgement for every Settings autosave. */
export function SettingsAutosaveNotice() {
  const [message, setMessage] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onSaved = (event: Event) => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      if (removeTimer.current) clearTimeout(removeTimer.current);
      setLeaving(false);
      setMessage((event as CustomEvent<string>).detail || 'Settings saved');
      fadeTimer.current = setTimeout(() => {
        setLeaving(true);
        removeTimer.current = setTimeout(() => setMessage(null), 180);
      }, 1320);
    };
    window.addEventListener(SUCCESS_EVENT, onSaved);
    return () => {
      window.removeEventListener(SUCCESS_EVENT, onSaved);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      if (removeTimer.current) clearTimeout(removeTimer.current);
    };
  }, []);

  return message ? (
    <div className={`settings-autosave-notice${leaving ? ' is-leaving' : ''}`} role="status">
      {message}
    </div>
  ) : null;
}

interface AutosaveOptions<T> {
  value: T;
  save: (value: T) => Promise<void>;
  what: string;
  enabled: boolean;
  delay?: number;
  validate?: (value: T) => string | null;
}

/** Serialised trailing-edge autosave. New edits made during a request are written next. */
export function useSettingsAutosave<T>({ value, save, what, enabled, delay = 600, validate }: AutosaveOptions<T>) {
  const [draft, setDraftState] = useState(value);
  const [status, setStatus] = useState<AutosaveState>('clean');
  const [error, setError] = useState<string | null>(null);
  const draftRef = useRef(draft);
  const savedRef = useRef(value);
  const savingRef = useRef(false);
  const queuedRef = useRef(false);
  const failedRef = useRef<{ value: T } | null>(null);
  const mountedRef = useRef(true);
  const same = useCallback((left: T, right: T) => JSON.stringify(left) === JSON.stringify(right), []);
  draftRef.current = draft;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  useEffect(() => {
    if (!same(draftRef.current, savedRef.current)) return;
    savedRef.current = value;
    failedRef.current = null;
    setDraftState(value);
    setStatus('clean');
    setError(null);
  }, [same, value]);

  const run = useCallback(async () => {
    if (!enabled || savingRef.current) return;
    const next = draftRef.current;
    const validationError = validate?.(next) ?? null;
    if (validationError) {
      setStatus('invalid');
      setError(validationError);
      return;
    }
    if (same(next, savedRef.current)) return;
    savingRef.current = true;
    queuedRef.current = false;
    setStatus('saving');
    setError(null);
    try {
      await save(next);
      savedRef.current = next;
      failedRef.current = null;
      if (mountedRef.current) {
        setStatus('saved');
        announceSettingsSaved();
      }
    } catch (caught) {
      failedRef.current = { value: next };
      if (mountedRef.current) {
        setStatus('failed');
        setError(caught instanceof Error ? caught.message : String(caught));
      }
    } finally {
      savingRef.current = false;
      // A rejected value remains failed until the operator changes it. Only a newer edit
      // queued while this request was in flight may start another request here.
      if (queuedRef.current) void run();
    }
  }, [enabled, same, save, validate]);

  useEffect(() => {
    if (!enabled || same(draft, savedRef.current)) return;
    if (failedRef.current && same(draft, failedRef.current.value)) return;
    const validationError = validate?.(draft) ?? null;
    setStatus(validationError ? 'invalid' : 'pending');
    setError(validationError);
    const timer = validationError ? null : setTimeout(() => void run(), delay);
    return () => { if (timer) clearTimeout(timer); };
  }, [delay, draft, enabled, run, same, validate]);

  const dirtyRef = useRef(false);
  dirtyRef.current = !same(draft, savedRef.current);
  useEffect(() => registerUnsavedEditor({ isDirty: () => dirtyRef.current, what }), [what]);

  const setDraft = useCallback((action: SetStateAction<T>) => {
    const next = typeof action === 'function'
      ? (action as (current: T) => T)(draftRef.current)
      : action;
    if (failedRef.current && !same(next, failedRef.current.value)) failedRef.current = null;
    draftRef.current = next;
    setDraftState(next);
    if (savingRef.current) queuedRef.current = true;
  }, [same]);

  return { draft, setDraft, status, error, flush: run, dirty: dirtyRef.current };
}
